import type { SubscriptionStatus } from '@prospero/shared';
import type {
  MembershipSearchResponse,
  WorkspaceRelation,
} from '@shared/dto/auth/membership-search.response.dto';
import type { SearchMembershipsQueryDto } from '@shared/dto/auth/search-memberships.query.dto';
import { inject } from 'inversify';
import { Injectable } from '../../decorators/injectable';
import { HttpService, type IHttpService } from '../http/http.service';

interface MeResponse {
  id: string;
  email: string;
  firstName: string;
  lastName: string | null;
  roleName?: string | null;
  currentTenantId?: string;
  currentTenantName?: string;
  defaultLanguage?: string | null;
}

interface ProfileResponse {
  id: string;
  email: string;
  firstName: string;
  lastName: string | null;
  lastLoginAt: string | null;
  role: { name: string };
  tenant: { name: string; defaultLanguage: string; partitaIva: string | null } | null;
  // null means the user has no override and inherits `tenant.defaultLanguage`.
  language: string | null;
}

export interface MembershipResponse {
  tenantId: string;
  tenantName: string;
  roleId: string;
  roleName: string;
  /** Stato abbonamento del tenant: marca "in prova" nei selettori azienda. */
  subscriptionStatus: SubscriptionStatus;
  trialEndsAt: string | null;
  /** Azienda creata da un consulente (anche un collega): nessun abbonamento proprio. */
  isManaged: boolean;
  /** A gestirla e' proprio l'utente che legge. */
  isManagedByMe: boolean;
  /** Il consulente che l'ha creata, per mostrare "Gestita da {nome}". */
  managedBy: { userId: string; displayName: string } | null;
  /** Studio o azienda del partner del consulente designato (elencati per primi); null per i clienti. */
  workspaceRelation: WorkspaceRelation | null;
}

export interface MembershipsResponse {
  memberships: MembershipResponse[];
  /** Flag User-level: il back office salta la scelta dell'azienda. */
  isPlatformAdmin: boolean;
  /** True quando almeno una membership da consulente è bloccata dalla regola del partner attivo. */
  consultantAccessBlocked: boolean;
}

export interface IAuthService {
  getCurrentUser(): Promise<MeResponse>;
  changePassword(newPassword: string): Promise<void>;
  getProfile(): Promise<ProfileResponse>;
  updateProfileName(firstName: string, lastName: string): Promise<void>;
  updateProfileLanguage(language: string | null): Promise<void>;
  checkEmail(email: string): Promise<{ exists: boolean }>;
  getStripeConfig(): Promise<StripeConfigResponse>;
  getMemberships(): Promise<MembershipsResponse>;
  searchMemberships(query: SearchMembershipsQueryDto): Promise<MembershipSearchResponse>;
}

export interface StripeConfigResponse {
  publishableKey: string;
  monthlyPrice: number;
  annualPrice: number;
  trialDays: number;
}

@Injectable()
class AuthService implements IAuthService {
  constructor(@inject(HttpService) private readonly httpService: IHttpService) {}

  async checkEmail(email: string): Promise<{ exists: boolean }> {
    return this.httpService.get<{ exists: boolean }>(
      `auth/check-email?email=${encodeURIComponent(email)}`,
    );
  }

  async getStripeConfig(): Promise<StripeConfigResponse> {
    return this.httpService.get<StripeConfigResponse>('auth/stripe-config');
  }

  async getCurrentUser(): Promise<MeResponse> {
    return this.httpService.get<MeResponse>('auth/me');
  }

  async changePassword(newPassword: string): Promise<void> {
    await this.httpService.patch('account/change-password', { newPassword });
  }

  async getProfile(): Promise<ProfileResponse> {
    return this.httpService.get<ProfileResponse>('auth/profile');
  }

  async updateProfileName(firstName: string, lastName: string): Promise<void> {
    await this.httpService.patch('account/profile-name', { firstName, lastName });
  }

  async updateProfileLanguage(language: string | null): Promise<void> {
    await this.httpService.patch('account/profile-language', { language });
  }

  async getMemberships(): Promise<MembershipsResponse> {
    return this.httpService.get<MembershipsResponse>('auth/memberships');
  }

  async searchMemberships(query: SearchMembershipsQueryDto): Promise<MembershipSearchResponse> {
    const searchParams = new URLSearchParams();
    if (query.search) {
      searchParams.set('search', query.search);
    }
    for (const status of query.subscriptionStatus ?? []) {
      searchParams.append('subscriptionStatus', status);
    }
    if (query.provincia) {
      searchParams.set('provincia', query.provincia);
    }
    if (query.page !== undefined) {
      searchParams.set('page', String(query.page));
    }
    if (query.pageSize !== undefined) {
      searchParams.set('pageSize', String(query.pageSize));
    }
    return this.httpService.get<MembershipSearchResponse>('auth/memberships/search', {
      searchParams,
    });
  }
}

export { AuthService };
